import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ConfigNeeded } from "@/components/ConfigNeeded";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function AssistantIA() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/ai/status"); setStatus(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId });
      setMessages((m) => [...m, { role: "ai", text: data.reply || "" }]);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      setMessages((m) => [...m, { role: "error", text: detail }]);
      toast.error(detail);
    }
  };

  return (
    <div>
      <PageHeader title="Assistant IA" subtitle="Votre copilote pour rédiger devis, messages et idées marketing" icon={Sparkles} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
      <div className="space-y-4">
        {!status?.configured && (
          <ConfigNeeded title="Configuration nécessaire" testid="ai-config-needed">
            L'assistant IA n'est pas encore connecté. Ajoutez la clé <span className="font-mono">AI_API_KEY</span> dans l'onglet Secrets pour activer des réponses réelles. Aucune réponse n'est simulée.
          </ConfigNeeded>
        )}

        <div className="bg-white border border-slate-200 rounded-xl flex flex-col h-[60vh]">
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="text-sm text-slate-400 text-center mt-8">Posez une question pour commencer la conversation.</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-emerald-500 text-black" : m.role === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-200 p-3 flex items-center gap-2">
            <Input data-testid="ai-input" value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>{ if (e.key === "Enter") send(); }} placeholder="Écrivez votre message…" className="bg-slate-50 border-slate-200" />
            <Button data-testid="ai-send" onClick={send} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
